// src/pages/AdminLanding.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import {
  Users,
  Plus,
  Save,
  Trash2,
  Eye,
  EyeOff,
  ArrowLeft,
  Gift,
} from "lucide-react";

const BG = "#0C0C1A";
const CARD = "#141428";
const BORDER = "#2A2A40";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

/* ------------------------------ Defaults ------------------------------ */
const DEFAULT_LANDING_PROMO = {
  enabled: true,
  headline: "Try PixelAds.ai 100% free",
  subtext: "No credit card required!",
  links: [],
};

const DEFAULT_APP_PROMO = {
  enabled: true,
  headline: "New: Your plan resets every month",
  subtext: "Generate again when your monthly quota refreshes.",
  links: [],
};

/* ------------------------------ Helpers ------------------------------ */
function uid() {
  try {
    // modern browsers
    return window.crypto?.randomUUID?.() || `l_${Date.now()}_${Math.random()}`;
  } catch (_e) {
    return `l_${Date.now()}_${Math.random()}`;
  }
}

function asString(v, fallback = "") {
  if (v === null || v === undefined) return fallback;
  return String(v);
}

// Matches things like: youtube.com/..., www.youtube.com/..., foo.bar/path
function looksLikeDomainLike(h) {
  if (!h) return false;
  if (/\s/.test(h)) return false;
  return /^[a-z0-9][a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i.test(h);
}

function normalizeHref(rawHref) {
  const h = asString(rawHref, "").trim();
  if (!h) return "";

  const lower = h.toLowerCase();

  if (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("/") ||
    lower.startsWith("#") ||
    lower.startsWith("mailto:")
  ) {
    return h;
  }

  if (looksLikeDomainLike(h)) return `https://${h}`;

  return h;
}

function ensureLinkIds(promo) {
  const p = promo || {};
  const links = Array.isArray(p.links) ? p.links : [];
  const nextLinks = links.map((l) => ({
    id: l?.id || uid(),
    label: asString(l?.label, ""),
    href: asString(l?.href, ""),
    newTab: !!l?.newTab,
  }));
  return { ...p, links: nextLinks };
}

function sanitizePromoForSave(promo) {
  const p = promo || {};
  const links = Array.isArray(p.links) ? p.links : [];

  const outLinks = links
    .map((l) => {
      const label = asString(l?.label, "").trim().slice(0, 48);
      const href = normalizeHref(l?.href).trim().slice(0, 500);
      const newTab = !!l?.newTab;
      const id = l?.id || uid();
      return { id, label, href, newTab };
    })
    .filter((l) => l.label || l.href);

  return {
    enabled: typeof p.enabled === "boolean" ? p.enabled : true,
    headline: asString(p.headline, "").slice(0, 120),
    subtext: asString(p.subtext, "").slice(0, 200),
    links: outLinks,
  };
}

function FieldLabel({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-400">
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-pink-500/60"
    />
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 border text-xs font-semibold ${
        checked
          ? "bg-emerald-600/15 text-emerald-300 border-emerald-700/40"
          : "bg-zinc-800/50 text-zinc-300 border-zinc-700/50"
      }`}
    >
      {checked ? <Eye size={14} /> : <EyeOff size={14} />}
      <span>{label}</span>
    </button>
  );
}

/* ------------------------------ Promo Editor ------------------------------ */
function PromoEditor({
  title,
  description,
  promo,
  setPromo,
  loading,
  saving,
  err,
  okMsg,
  onSave,
}) {
  const previewLinks = useMemo(() => promo.links || [], [promo.links]);

  const updateLink = (linkId, patch) => {
    setPromo((prev) => {
      const next = ensureLinkIds(prev);
      next.links = (next.links || []).map((l) =>
        l.id === linkId ? { ...l, ...patch } : l
      );
      return next;
    });
  };

  const addLink = () => {
    setPromo((prev) => {
      const next = ensureLinkIds(prev);
      next.links = [
        ...(next.links || []),
        { id: uid(), label: "", href: "", newTab: false },
      ];
      return next;
    });
  };

  const removeLink = (linkId) => {
    setPromo((prev) => {
      const next = ensureLinkIds(prev);
      next.links = (next.links || []).filter((l) => l.id !== linkId);
      return next;
    });
  };

  return (
    <div
      className="rounded-3xl p-5 sm:p-6"
      style={{ background: CARD, border: `1px solid ${BORDER}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-zinc-100 font-semibold text-lg">{title}</div>
          <div className="mt-1 text-sm text-zinc-400">{description}</div>
        </div>

        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-60"
        >
          <Save size={16} />
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {err && (
        <div className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {err}
        </div>
      )}
      {okMsg && (
        <div className="mt-4 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
          {okMsg}
        </div>
      )}

      <div className="mt-6 flex items-center gap-2">
        <Toggle
          checked={!!promo.enabled}
          onChange={(v) => setPromo((p) => ({ ...p, enabled: v }))}
          label={promo.enabled ? "Promo visible" : "Promo hidden"}
        />
      </div>

      <div className="mt-6 grid gap-4">
        <div>
          <FieldLabel>Headline</FieldLabel>
          <div className="mt-2">
            <Input
              value={promo.headline || ""}
              onChange={(v) => setPromo((p) => ({ ...p, headline: v }))}
              placeholder="Headline"
            />
          </div>
        </div>

        <div>
          <FieldLabel>Subtext</FieldLabel>
          <div className="mt-2">
            <Input
              value={promo.subtext || ""}
              onChange={(v) => setPromo((p) => ({ ...p, subtext: v }))}
              placeholder="Subtext"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <FieldLabel>Links (optional)</FieldLabel>
            <button
              type="button"
              onClick={addLink}
              className="inline-flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-xs font-semibold text-zinc-200 hover:bg-white/10"
            >
              <Plus size={14} />
              Add link
            </button>
          </div>

          <div className="mt-3 space-y-3">
            {(promo.links || []).length === 0 ? (
              <div className="text-sm text-zinc-500">
                No links yet. Add one if you want “Pricing”, “Start free”, etc.
              </div>
            ) : (
              (promo.links || []).map((l) => (
                <div
                  key={l.id}
                  className="rounded-2xl border border-white/10 bg-black/30 p-3"
                >
                  <div className="grid gap-3 sm:grid-cols-[1fr,1.4fr,auto] items-center">
                    <div>
                      <FieldLabel>Label</FieldLabel>
                      <div className="mt-2">
                        <Input
                          value={l?.label || ""}
                          onChange={(v) => updateLink(l.id, { label: v })}
                          placeholder="Pricing"
                        />
                      </div>
                    </div>

                    <div>
                      <FieldLabel>Href</FieldLabel>
                      <div className="mt-2">
                        <Input
                          value={l?.href || ""}
                          onChange={(v) => updateLink(l.id, { href: v })}
                          placeholder="#pricing or /signup or https://..."
                        />
                        <div className="mt-1 text-[11px] text-zinc-500">
                          Tip: you can paste “youtube.com/…” and it will be saved
                          as https://…
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-2 mt-1 sm:mt-6">
                      <button
                        type="button"
                        onClick={() => updateLink(l.id, { newTab: !l?.newTab })}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                          l?.newTab
                            ? "bg-indigo-600/15 text-indigo-200 border-indigo-600/40"
                            : "bg-zinc-800/50 text-zinc-300 border-zinc-700/50"
                        }`}
                      >
                        {l?.newTab ? "New tab" : "Same tab"}
                      </button>

                      <button
                        type="button"
                        onClick={() => removeLink(l.id)}
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border bg-rose-600/15 text-rose-200 border-rose-600/40 hover:bg-rose-600/20"
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="pt-2">
          <FieldLabel>Preview</FieldLabel>
          <div className="mt-2 rounded-2xl overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-[#F3123C] via-[#FC2783] to-[#605DFF] text-white text-xs sm:text-sm">
              <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 px-4 py-2">
                <span className="text-center sm:text-left">
                  <span className="font-semibold underline underline-offset-2">
                    {promo.headline || ""}
                  </span>
                  <span className="ml-1">{promo.subtext || ""}</span>
                </span>

                {previewLinks.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                    {previewLinks
                      .filter((x) => x?.label && x?.href)
                      .slice(0, 5)
                      .map((x) => (
                        <span
                          key={x.id || `${x.label}-${x.href}`}
                          className="inline-flex items-center gap-1 text-white/95 underline underline-offset-4 decoration-white/60"
                          title={normalizeHref(x.href)}
                        >
                          <span className="font-semibold">{x.label}</span>
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
      </div>
    </div>
  );
}

export default function AdminLanding() {
  const { getToken } = useAuth();

  // Landing promo
  const [landingLoading, setLandingLoading] = useState(false);
  const [landingSaving, setLandingSaving] = useState(false);
  const [landingErr, setLandingErr] = useState("");
  const [landingOk, setLandingOk] = useState("");
  const [landingPromo, setLandingPromo] = useState(
    ensureLinkIds(DEFAULT_LANDING_PROMO)
  );

  // App promo (logged-in pages)
  const [appLoading, setAppLoading] = useState(false);
  const [appSaving, setAppSaving] = useState(false);
  const [appErr, setAppErr] = useState("");
  const [appOk, setAppOk] = useState("");
  const [appPromo, setAppPromo] = useState(ensureLinkIds(DEFAULT_APP_PROMO));

  async function loadLandingPromo() {
    setLandingLoading(true);
    setLandingErr("");
    setLandingOk("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/landing/promo-strip`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to load landing promo");
      setLandingPromo(ensureLinkIds(json?.promo || DEFAULT_LANDING_PROMO));
    } catch (e) {
      setLandingErr(e?.message || "Failed to load landing promo");
    } finally {
      setLandingLoading(false);
    }
  }

  async function saveLandingPromo() {
    setLandingSaving(true);
    setLandingErr("");
    setLandingOk("");
    try {
      const token = await getToken();
      const payload = sanitizePromoForSave(ensureLinkIds(landingPromo));

      const res = await fetch(`${API_BASE}/api/admin/landing/promo-strip`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to save landing promo");
      setLandingPromo(ensureLinkIds(json?.promo || payload));
      setLandingOk("Saved.");
    } catch (e) {
      setLandingErr(e?.message || "Failed to save landing promo");
    } finally {
      setLandingSaving(false);
      setTimeout(() => setLandingOk(""), 2500);
    }
  }

  async function loadAppPromo() {
    setAppLoading(true);
    setAppErr("");
    setAppOk("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/app/promo-strip`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to load app promo");
      setAppPromo(ensureLinkIds(json?.promo || DEFAULT_APP_PROMO));
    } catch (e) {
      setAppErr(e?.message || "Failed to load app promo");
    } finally {
      setAppLoading(false);
    }
  }

  async function saveAppPromo() {
    setAppSaving(true);
    setAppErr("");
    setAppOk("");
    try {
      const token = await getToken();
      const payload = sanitizePromoForSave(ensureLinkIds(appPromo));

      const res = await fetch(`${API_BASE}/api/admin/app/promo-strip`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to save app promo");
      setAppPromo(ensureLinkIds(json?.promo || payload));
      setAppOk("Saved.");
    } catch (e) {
      setAppErr(e?.message || "Failed to save app promo");
    } finally {
      setAppSaving(false);
      setTimeout(() => setAppOk(""), 2500);
    }
  }

  useEffect(() => {
    loadLandingPromo();
    loadAppPromo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen" style={{ background: BG }}>
      <div
        className="sticky top-0 z-20 px-4 py-3"
        style={{ background: BG, borderBottom: `1px solid ${BORDER}` }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Back to Dashboard */}
            <Link
              to="/generate"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10 hover:text-white text-sm"
              aria-label="Back to Dashboard"
              title="Back to Dashboard"
            >
              <ArrowLeft size={16} />
              Dashboard
            </Link>

            <Link
              to="/admin/users"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10 hover:text-white text-sm"
              aria-label="Go to Admin Users"
              title="Go to Admin Users"
            >
              <Users size={16} />
              Users
            </Link>

            {/* ✅ NEW: Promo Modal button */}
            <Link
              to="/admin/promo-modal"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10 hover:text-white text-sm"
              aria-label="Go to Promo Modal"
              title="Go to Promo Modal"
            >
              <Gift size={16} />
              Promo Modal
            </Link>

            <div className="text-zinc-100 font-semibold">Promo Banners</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <PromoEditor
          title="Landing promo strip"
          description="Controls the fixed top strip on the public landing page."
          promo={landingPromo}
          setPromo={setLandingPromo}
          loading={landingLoading}
          saving={landingSaving}
          err={landingErr}
          okMsg={landingOk}
          onSave={saveLandingPromo}
        />

        <PromoEditor
          title="App promo strip (logged-in pages)"
          description="Controls the fixed top strip shown across authenticated pages (dashboard/generate/etc)."
          promo={appPromo}
          setPromo={setAppPromo}
          loading={appLoading}
          saving={appSaving}
          err={appErr}
          okMsg={appOk}
          onSave={saveAppPromo}
        />
      </div>
    </div>
  );
}
